import React, { useState } from "react";
import { Form, Button, Alert } from "react-bootstrap";
import { useUser } from "../contexts/UserContext";
import DreamModal from "./DreamModal";
import { FaKey, FaEye, FaEyeSlash, FaShieldAlt } from "react-icons/fa";
import "./PinCreationModal.css";

const PinCreationModal = ({
  show,
  onHide,
  onPinCreated,
  title = "Create Account PIN",
}) => {
  const { user, savePIN } = useUser();
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [error, setError] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!pin.trim()) {
      setError("Please create a PIN");
      return;
    }

    if (pin.length < 4) {
      setError("PIN must be at least 4 characters");
      return;
    }

    if (pin !== confirmPin) {
      setError("PINs do not match");
      return;
    }

    try {
      setIsCreating(true);
      console.log("Creating PIN for user:", user.email);

      // Save PIN to Firestore (hashed)
      await savePIN(pin);

      console.log("PIN created successfully");

      // Reset form
      setPin("");
      setConfirmPin("");
      setError("");
      setShowPin(false);
      setShowConfirmPin(false);

      // Notify parent component
      if (onPinCreated) {
        onPinCreated();
      }

      onHide();
    } catch (err) {
      console.error("Error creating PIN:", err);
      setError("Failed to create PIN. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    setPin("");
    setConfirmPin("");
    setError("");
    setShowPin(false);
    setShowConfirmPin(false);
    onHide();
  };

  return (
    <DreamModal
      show={show}
      onHide={handleCancel}
      title={
        <>
          <FaShieldAlt className="me-2" />
          {title}
        </>
      }
      size="md"
      className="pin-creation-modal"
    >
      <div className="pin-creation-content">
        <div className="text-center mb-4">
          <div className="pin-creation-icon-wrapper">
            <FaKey className="pin-creation-icon" />
          </div>
          <h5>Secure Your Account</h5>
          <p className="text-muted">
            Create a PIN to protect your account settings and subscription
            management
          </p>
        </div>

        <Form onSubmit={handlePinSubmit}>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Create Account PIN</Form.Label>
                <div className="pin-input-wrapper">
                  <Form.Control
                    type={showPin ? "text" : "password"}
                    placeholder="Enter 4+ digit PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    className="pin-input"
                    autoFocus
                    disabled={isCreating}
                  />
                  <Button
                    variant="outline-secondary"
                    className="pin-toggle-btn"
                    onClick={() => setShowPin(!showPin)}
                    type="button"
                    disabled={isCreating}
                  >
                    {showPin ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-4">
                <Form.Label>Confirm PIN</Form.Label>
                <div className="pin-input-wrapper">
                  <Form.Control
                    type={showConfirmPin ? "text" : "password"}
                    placeholder="Confirm your PIN"
                    value={confirmPin}
                    onChange={(e) => setConfirmPin(e.target.value)}
                    className="pin-input"
                    disabled={isCreating}
                  />
                  <Button
                    variant="outline-secondary"
                    className="pin-toggle-btn"
                    onClick={() => setShowConfirmPin(!showConfirmPin)}
                    type="button"
                    disabled={isCreating}
                  >
                    {showConfirmPin ? <FaEyeSlash /> : <FaEye />}
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  This PIN will be required for account changes
                </Form.Text>
              </Form.Group>
            </div>
          </div>

          <div className="pin-creation-actions">
            <Button
              variant="secondary"
              onClick={handleCancel}
              className="cancel-pin-creation-btn"
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="create-pin-btn"
              disabled={isCreating}
            >
              {isCreating ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Creating PIN...
                </>
              ) : (
                "Create PIN"
              )}
            </Button>
          </div>
        </Form>
      </div>
    </DreamModal>
  );
};

export default PinCreationModal;
